// src/pages/MyInfo.js
import React, { useEffect, useState } from 'react';
import './MyInfo.css';
import { apiFetch } from '../api';
import { Link, useNavigate } from 'react-router-dom';

const formatDateTime = (dt) => {
  if (!dt) return '—';
  try {
    const date = new Date(dt);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return dt;
  }
};

const MyInfo = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      // 如果服务端提供了注销接口，可以调用；失败也不阻断本地退出
      try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch (_) {}
      localStorage.removeItem('token');
      localStorage.removeItem('nickname');
      setNeedsLogin(true);
      navigate('/login');
    } catch (e) {
      // 兜底：就算异常也清理 token
      localStorage.removeItem('token');
      localStorage.removeItem('nickname');
      setNeedsLogin(true);
      navigate('/login');
    }
  };

  useEffect(() => {
    let mounted = true;

    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || null;
    if (!token) {
      setNeedsLogin(true);
      setLoading(false);
      return;
    }

    const fetchInfo = async () => {
      try {
        setLoading(true);
        const res = await apiFetch('/api/auth/me');
        if (!mounted) return;
        setInfo({
          nickname: res?.user?.nickname || '未设置',
          phone: res?.user?.phone || '—',
          email: res?.user?.email || '—',
          balance: res?.user?.balance ?? 0,
          lastLogin: res?.user?.last_login_at || '—',
        });
        setNeedsLogin(false);
      } catch (e) {
        if (!mounted) return;
        const msg = String(e?.status || e?.code || e?.message || '').toLowerCase();
        if (msg.includes('401') || msg.includes('unauthorized')) {
          setNeedsLogin(true);
        } else {
          setError('加载失败，请稍后重试');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchInfo();
    return () => { mounted = false; };
  }, []);

  if (needsLogin) {
    return (
      <div className="myinfo-page theme-gate">
        <section className="myinfo-gate-card">
          <div className="myinfo-gate-icon" aria-hidden>🔒</div>
          <h2 className="myinfo-gate-title">尚未登录</h2>
          <p className="myinfo-gate-subtitle">登录后即可查看你的账户信息与积分</p>
          <div className="myinfo-gate-actions">
            <button className="myinfo-btn myinfo-btn-primary" onClick={() => navigate('/login')}>去登录</button>
            <Link className="myinfo-btn myinfo-btn-ghost" to="/register">没有账号？去注册</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="myinfo-page theme-light">
      <div className="myinfo-container">
        <div className="myinfo-hero">
          <div>
            <h2 className="myinfo-title">我的信息</h2>
            <p className="myinfo-subtitle">本源AI · 简单高效</p>
          </div>
          <div className="myinfo-actions">
            <Link className="myinfo-btn myinfo-btn-ghost" to="/recharge">充值中心</Link>
            <button className="myinfo-btn myinfo-btn-danger" onClick={handleLogout}>退出</button>
          </div>
        </div>

        {loading ? (
          <div className="myinfo-card myinfo-glass loading">
            <div className="myinfo-skeleton-row"></div>
            <div className="myinfo-skeleton-row"></div>
            <div className="myinfo-skeleton-row"></div>
            <div className="myinfo-skeleton-row"></div>
            <div className="myinfo-skeleton-row"></div>
          </div>
        ) : (
          <div className="myinfo-card myinfo-glass">
            <div className="myinfo-item">
              <span className="myinfo-label">昵称</span>
              <span className="myinfo-value">{info?.nickname}</span>
            </div>
            <div className="myinfo-item">
              <span className="myinfo-label">手机号</span>
              <span className="myinfo-value">{info?.phone}</span>
            </div>
            <div className="myinfo-item">
              <span className="myinfo-label">邮箱</span>
              <span className="myinfo-value">{info?.email}</span>
            </div>
            <div className="myinfo-item">
              <span className="myinfo-label">积分</span>
              <span className="myinfo-value myinfo-highlight">{info?.balance}</span>
            </div>
            <div className="myinfo-item">
              <span className="myinfo-label">上次登录</span>
              <span className="myinfo-value">{formatDateTime(info?.lastLogin)}</span>
            </div>
            {error && <div className="myinfo-error-tip" role="alert">{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyInfo;
